import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Teacher, isTeacher } from '../../../utils/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  orderBy, 
  Timestamp,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { getSubjectName } from '../../../utils/subjectUtils';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import TrashIcon from '../../../components/icons/TrashIcon';
import DownIcon from '../../../components/icons/DownIcon';
import PlusIcon from '../../../components/icons/PlusIcon';
import EditIcon from '../../../components/icons/EditIcon';
import ThreeDotLengthIcon from '../../../components/icons/3DotLengthIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon';

interface Lesson {
  id: string;
  grade: number;
  class: number;
  date: Timestamp;
  period: number;
  progressPage: string;
  progressStatus: string;
  assignment: string;
  progressPercentage: number;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface ClassProgress {
  grade: number;
  class: number;
  progressPercentage: number;
}

interface SubjectClass {
  grade: number;
  class: number;
}

const SubjectLessonManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [subjectClasses, setSubjectClasses] = useState<SubjectClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // 탭 상태
  const [activeTab, setActiveTab] = useState<'progress' | 'plan'>('progress');
  
  // 필터 상태
  const [selectedGrade, setSelectedGrade] = useState<1 | 2 | 3>(1);
  const [selectedSemester, setSelectedSemester] = useState<'1' | '2'>('1');
  const [selectedWeek, setSelectedWeek] = useState(5);
  const [viewMode, setViewMode] = useState<'today' | 'all'>('today');
  
  // 반별 카드 필터 상태
  const [filterGrade, setFilterGrade] = useState<number | null>(null);
  const [filterClass, setFilterClass] = useState<number | null>(null);
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());
  const [gradeDropdownOpen, setGradeDropdownOpen] = useState(false);
  const [classDropdownOpen, setClassDropdownOpen] = useState(false);
  
  // 진도 입력 폼 상태
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [selectedClass, setSelectedClass] = useState<{grade: number, class: number} | null>(null);
  const [progressPage, setProgressPage] = useState('');
  const [progressStatus, setProgressStatus] = useState('');
  const [assignment, setAssignment] = useState('');
  const [progressPageDropdownOpen, setProgressPageDropdownOpen] = useState(false);
  const [assignmentDropdownOpen, setAssignmentDropdownOpen] = useState(false);
  const [classFormDropdownOpen, setClassFormDropdownOpen] = useState(false);
  const [showAddProgressModal, setShowAddProgressModal] = useState(false);

  // 담당 교실 목록 로드
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;

    const loadSubjectClasses = async () => {
      try {
        // 교과별 학생들로부터 교실 정보 추출
        const studentsRef = collection(
          db,
          `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/students`
        );
        const studentsSnapshot = await getDocs(studentsRef);
        
        const classesMap = new Map<string, { grade: number; class: number }>();
        
        studentsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const grade = data.grade || 1;
          const classNum = data.class || 1;
          const key = `${grade}_${classNum}`;
          
          if (!classesMap.has(key)) {
            classesMap.set(key, { grade, class: classNum });
          }
        });

        const classesArray = Array.from(classesMap.values());
        setSubjectClasses(classesArray);
      } catch (err) {
        console.error('교실 목록 로드 오류:', err);
      }
    };

    loadSubjectClasses();
  }, [currentUser]);

  // 수업 데이터 로드
  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) return;

    const loadLessons = async () => {
      try {
        setLoading(true);
        const lessonsRef = collection(
          db, 
          `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/lessons`
        );
        const q = query(lessonsRef, orderBy('date', 'desc'), orderBy('period', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const lessonsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Lesson[];
          setLessons(lessonsData);
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (err) {
        console.error('수업 데이터 로드 오류:', err);
        setError('수업 데이터를 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    };

    loadLessons();
  }, [currentUser]);

  // 진도 현황 계산
  const classProgress = useMemo(() => {
    const progressMap = new Map<string, ClassProgress>();
    
    lessons.forEach(lesson => {
      const key = `${lesson.grade}-${lesson.class}`;
      const existing = progressMap.get(key);
      
      if (!existing || lesson.progressPercentage > existing.progressPercentage) {
        progressMap.set(key, {
          grade: lesson.grade,
          class: lesson.class,
          progressPercentage: lesson.progressPercentage
        });
      }
    });

    return Array.from(progressMap.values());
  }, [lessons]);

  // 선택된 학년의 반별 진행도
  const gradeClassProgress = useMemo(() => {
    return classProgress
      .filter(p => p.grade === selectedGrade)
      .sort((a, b) => a.class - b.class);
  }, [classProgress, selectedGrade]);

  // 오늘 보기 필터링
  const filteredLessons = useMemo(() => {
    let filtered = lessons;
    
    if (viewMode === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      filtered = lessons.filter(lesson => {
        const lessonDate = lesson.date.toDate();
        return lessonDate >= today && lessonDate < tomorrow;
      });
    }
    
    return filtered.sort((a, b) => {
      const dateCompare = b.date.toMillis() - a.date.toMillis();
      if (dateCompare !== 0) return dateCompare;
      return a.period - b.period;
    });
  }, [lessons, viewMode]);

  // 진도 등록
  const handleSubmit = async () => {
    if (!currentUser || !isTeacher(currentUser) || !selectedClass || !selectedPeriod) {
      setError('학급과 교시를 선택해주세요.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const lessonsRef = collection(
        db,
        `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/lessons`
      );

      const date = new Date(selectedDate);
      date.setHours(0, 0, 0, 0);

      await addDoc(lessonsRef, {
        grade: selectedClass.grade,
        class: selectedClass.class,
        date: Timestamp.fromDate(date),
        period: selectedPeriod,
        progressPage: progressPage || '',
        progressStatus: progressStatus || '',
        assignment: assignment || '',
        progressPercentage: 0, // 추후 계산 로직 추가 가능
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setSuccess('진도가 등록되었습니다.');
      setProgressPage('');
      setProgressStatus('');
      setAssignment('');
      setSelectedPeriod(null);
      setSelectedClass(null);
      setShowAddProgressModal(false);
      
      setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('진도 등록 오류:', err);
        setError('진도를 등록하는 중 오류가 발생했습니다.');
        setTimeout(() => setError(null), 3000);
      }
  };

  // 진도 삭제
  const handleDelete = async (lessonId: string) => {
    if (!currentUser || !isTeacher(currentUser)) return;
    if (!window.confirm('이 진도 기록을 삭제하시겠습니까?')) return;

    try {
      const lessonRef = doc(
        db,
        `schools/${currentUser.schoolId}/subjects/${currentUser.subjectId}/lessons/${lessonId}`
      );
      await deleteDoc(lessonRef);
      setSuccess('진도가 삭제되었습니다.');
      setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('진도 삭제 오류:', err);
        setError('진도를 삭제하는 중 오류가 발생했습니다.');
        setTimeout(() => setError(null), 3000);
      }
  };

  // 날짜 포맷팅
  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const weekday = weekdays[date.getDay()];
    return `${year}.${month}.${day} (${weekday})`;
  };

  // 주차 계산 (간단한 예시)
  const getWeekNumber = (date: Date) => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  };

  // 반별로 수업 그룹화
  const lessonsByClass = useMemo(() => {
    const grouped = new Map<string, Lesson[]>();
    
    lessons.forEach(lesson => {
      const key = `${lesson.grade}-${lesson.class}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(lesson);
    });

    // 각 반의 수업을 날짜순으로 정렬
    grouped.forEach((classLessons, key) => {
      classLessons.sort((a, b) => {
        const dateCompare = b.date.toMillis() - a.date.toMillis();
        if (dateCompare !== 0) return dateCompare;
        return a.period - b.period;
      });
    });

    return grouped;
  }, [lessons]);

  // 반별 진도 요약 정보 계산
  const classSummaries = useMemo(() => {
    const summaries: Array<{
      grade: number;
      class: number;
      lessons: Lesson[];
      lastLessonDate: Date | null;
      progressPercentage: number;
      remainingLessons: number;
      schedule: string;
    }> = [];

    lessonsByClass.forEach((classLessons, key) => {
      const [grade, classNum] = key.split('-').map(Number);
      
      // 최근 수업일
      const lastLesson = classLessons[0];
      const lastLessonDate = lastLesson ? lastLesson.date.toDate() : null;

      // 진도율 (가장 높은 진행도)
      const progressPercentage = classLessons.length > 0
        ? Math.max(...classLessons.map(l => l.progressPercentage))
        : 0;

      // 남은 수업 횟수 (간단한 계산, 추후 개선 가능)
      const remainingLessons = Math.max(0, 10 - classLessons.length);

      // 수업 일정 (요일, 교시)
      const scheduleMap = new Map<string, number[]>();
      classLessons.forEach(lesson => {
        const date = lesson.date.toDate();
        const weekday = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
        if (!scheduleMap.has(weekday)) {
          scheduleMap.set(weekday, []);
        }
        scheduleMap.get(weekday)!.push(lesson.period);
      });
      
      const scheduleParts: string[] = [];
      scheduleMap.forEach((periods, weekday) => {
        const sortedPeriods = periods.sort((a, b) => a - b);
        scheduleParts.push(`${weekday}요일 ${sortedPeriods.join(', ')}교시`);
      });
      const schedule = scheduleParts.join(' | ');

      summaries.push({
        grade,
        class: classNum,
        lessons: classLessons,
        lastLessonDate,
        progressPercentage,
        remainingLessons,
        schedule
      });
    });

    // 학년, 반 순으로 정렬
    return summaries.sort((a, b) => {
      if (a.grade !== b.grade) return a.grade - b.grade;
      return a.class - b.class;
    });
  }, [lessonsByClass]);

  // 필터링된 반별 요약
  const filteredClassSummaries = useMemo(() => {
    return classSummaries.filter(summary => {
      if (filterGrade !== null && summary.grade !== filterGrade) return false;
      if (filterClass !== null && summary.class !== filterClass) return false;
      return true;
    });
  }, [classSummaries, filterGrade, filterClass]);

  // 카드 확장/축소 토글
  const toggleClassExpanded = (grade: number, classNum: number) => {
    const key = `${grade}-${classNum}`;
    setExpandedClasses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // 주차별로 진도 그룹화
  const groupLessonsByWeek = (lessons: Lesson[]) => {
    const grouped = new Map<number, Lesson[]>();
    
    lessons.forEach(lesson => {
      const week = getWeekNumber(lesson.date.toDate());
      if (!grouped.has(week)) {
        grouped.set(week, []);
      }
      grouped.get(week)!.push(lesson);
    });

    // 주차별로 정렬
    const sortedWeeks = Array.from(grouped.keys()).sort((a, b) => b - a);
    return sortedWeeks.map(week => ({
      week,
      lessons: grouped.get(week)!.sort((a, b) => {
        const dateCompare = b.date.toMillis() - a.date.toMillis();
        if (dateCompare !== 0) return dateCompare;
        return a.period - b.period;
      })
    }));
  };

  // 날짜 변경
  const handleDateChange = (days: number) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
    setSelectedWeek(getWeekNumber(newDate));
  };

  // 외부 클릭으로 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setGradeDropdownOpen(false);
        setClassDropdownOpen(false);
        setClassFormDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentUser || !isTeacher(currentUser)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '20px'
      }}>
        <div style={{ 
          color: 'var(--Color-status-negative, #FF3838)', 
          padding: '20px', 
          border: '1px solid #FF3838', 
          borderRadius: '8px' 
        }}>
          교사 권한이 필요합니다.
        </div>
      </div>
    );
  }

  const subjectName = currentUser.subjectId ? getSubjectName(currentUser.subjectId) : '교과';
  const currentWeek = getWeekNumber(selectedDate);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      position: 'relative',
      background: 'var(--Color-background-main, #F4F6FA)',
      overflow: 'auto'
    }}>
      {/* 상단 헤더 */}
      <div style={{
        width: '100%',
        maxWidth: '1280px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: 0
      }}>
        {/* 제목 영역 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <h1 style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '32px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '48px',
              margin: 0
            }}>
              수업 관리
            </h1>
            <h1 style={{
              color: 'var(--Color-text-brand-color, #FF6F0F)',
              fontSize: '32px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '48px',
              margin: 0
            }}>
              {subjectName}
            </h1>
          </div>
          
          {/* 탭 버튼 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={() => setActiveTab('progress')}
              style={{
                width: 'auto',
                height: '40px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === 'progress' 
                  ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                  : 'transparent',
                color: activeTab === 'progress'
                  ? 'white'
                  : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '21px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              진도 관리
            </button>
            <button
              onClick={() => setActiveTab('plan')}
              style={{
                width: 'auto',
                height: '40px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === 'plan' 
                  ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                  : 'transparent',
                color: activeTab === 'plan'
                  ? 'white'
                  : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '21px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              수업 계획
            </button>
          </div>
        </div>

        {/* 메인 컨텐츠 */}
        {activeTab === 'progress' ? (
        <div style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'stretch',
          width: '100%',
          minWidth: 0
        }}>
          {/* 왼쪽: 진도 현황 */}
          <Card
            width="411px"
            height="666px"
            padding="20px"
            gap="10px"
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '92px',
              width: '100%',
              height: '100%'
            }}>
              {/* 헤더 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <h2 style={{
                  color: 'black',
                  fontSize: '18px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '27px',
                  margin: 0
                }}>
                  진도 현황
                </h2>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '20px'
                }}>
                  <div style={{
                    color: 'black',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '18px'
                  }}>
                    {selectedSemester}학기 {selectedWeek}주차
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {[1, 2, 3].map(grade => (
                      <div
                        key={grade}
                        onClick={() => setSelectedGrade(grade as 1 | 2 | 3)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                          cursor: 'pointer'
                        }}
                      >
                        {selectedGrade === grade ? (
                          <CheckCircleIcon width={16} height={16} color="#FF6F0F" />
                        ) : (
                          <span style={{ fontSize: 16, color: '#BABABA' }}>○</span>
                        )}
                        <span style={{
                          color: selectedGrade === grade 
                            ? 'var(--Color-text-normal, #161616)' 
                            : 'var(--Color-text-assistive, #989898)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '18px'
                        }}>
                          {grade}학년
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 차트 영역 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                gap: '15px',
                flex: 1
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  gap: '24px',
                  width: '100%'
                }}>
                  {gradeClassProgress.length === 0 ? (
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      padding: '40px 0'
                    }}>
                      등록된 진도가 없습니다.
                    </div>
                  ) : (
                    gradeClassProgress.map((progress) => {
                      const percentage = progress.progressPercentage || 0;
                      const maxHeight = 430;
                      const height = (percentage / 100) * maxHeight;
                      
                      return (
                        <div
                          key={`${progress.grade}-${progress.class}`}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            width: '53px'
                          }}
                        >
                          {percentage > 0 && (
                            <div style={{
                              padding: '2px 12px',
                              background: percentage >= 50 ? '#FFD2B5' : '#DEE0E4',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px',
                              color: percentage >= 50 
                                ? 'var(--Color-text-brand-color, #FF6F0F)' 
                                : 'var(--Color-text-normal, #161616)'
                            }}>
                              {percentage}%
                            </div>
                          )}
                          <div
                            style={{
                              width: '53px',
                              height: `${height}px`,
                              background: percentage >= 50
                                ? 'linear-gradient(180deg, #FF9F5E 0%, rgba(255, 159, 94, 0.20) 100%)'
                                : '#F6F8FB',
                              borderRadius: '999px',
                              border: percentage >= 50
                                ? '1px solid #FF6F0F'
                                : '1px solid #DEE0E4',
                              minHeight: percentage > 0 ? '50px' : '0px'
                            }}
                          />
                          <div
                            style={{
                              width: '39px',
                              height: '39px',
                              background: percentage >= 50
                                ? 'var(--Color-fill-brand-color, #FF6F0F)'
                                : 'var(--Color-fill-normal, #293044)',
                              borderRadius: '9999px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '14px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '21px'
                            }}
                          >
                            {progress.class}반
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div style={{
                  textAlign: 'right',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '12px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  lineHeight: '18px',
                  width: '100%'
                }}>
                  ※ {selectedSemester}학기 남은 수업: 3주 (각 반별 약 10회)
                </div>
              </div>
            </div>
          </Card>

          {/* 오른쪽: 진도 입력 */}
          <Card
            width="845px"
            height="666px"
            padding="20px"
            gap="24px"
            style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              boxSizing: 'border-box'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
              width: '100%',
              height: '100%',
              boxSizing: 'border-box'
            }}>
              {/* 헤더 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '20px'
              }}>
                <h2 style={{
                  color: 'var(--Color-text-strong, #0D0D0D)',
                  fontSize: '18px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '27px',
                  margin: 0
                }}>
                  진도 입력
                </h2>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span
                    onClick={() => setViewMode('today')}
                    style={{
                      color: viewMode === 'today' 
                        ? 'var(--Color-text-strong, #0D0D0D)' 
                        : 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '21px',
                      cursor: 'pointer',
                      textDecoration: viewMode === 'today' ? 'underline' : 'none'
                    }}
                  >
                    오늘 보기
                  </span>
                  <span style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px'
                  }}>
                    |
                  </span>
                  <span
                    onClick={() => setViewMode('all')}
                    style={{
                      color: viewMode === 'all' 
                        ? 'var(--Color-text-strong, #0D0D0D)' 
                        : 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '21px',
                      cursor: 'pointer',
                      textDecoration: viewMode === 'all' ? 'underline' : 'none'
                    }}
                  >
                    전체 보기
                  </span>
                </div>
              </div>

              {/* 날짜 및 교시 선택 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px'
              }}>
                {/* 주차 정보 및 날짜 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <div style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '19.5px'
                  }}>
                    {selectedSemester}학기 {currentWeek}주차
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '46px'
                  }}>
                    <button
                      onClick={() => handleDateChange(-1)}
                      style={{
                        width: '30px',
                        height: '30px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <LeftArrowIcon width={30} height={30} />
                    </button>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '21px'
                      }}>
                        {selectedDate.getFullYear()}.{String(selectedDate.getMonth() + 1).padStart(2, '0')}.{String(selectedDate.getDate()).padStart(2, '0')}
                      </span>
                      <span style={{ fontSize: 20, color: '#161616' }}>📅</span>
                    </div>
                    <button
                      onClick={() => handleDateChange(1)}
                      style={{
                        width: '30px',
                        height: '30px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: 'rotate(180deg)'
                      }}
                    >
                      <LeftArrowIcon width={30} height={30} />
                    </button>
                  </div>
                </div>

                {/* 교시 선택 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '43px',
                  width: '100%',
                  maxWidth: '600px'
                }}>
                  {[1, 2, 3, 4, 5, 6, 7].map(period => {
                    const isSelected = selectedPeriod === period;
                    const todayLessons = filteredLessons.filter(l => {
                      const lessonDate = l.date.toDate();
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const tomorrow = new Date(today);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return lessonDate >= today && lessonDate < tomorrow && l.period === period;
                    });
                    const hasLesson = todayLessons.length > 0;
                    const lesson = hasLesson ? todayLessons[0] : null;
                    
                    return (
                      <div
                        key={period}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                          width: '30px'
                        }}
                      >
                        <div style={{
                          color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '18px'
                        }}>
                          {period}교시
                        </div>
                        <button
                          onClick={() => {
                            setSelectedPeriod(period);
                            if (lesson) {
                              setSelectedClass({ grade: lesson.grade, class: lesson.class });
                              setProgressPage(lesson.progressPage || '');
                              setProgressStatus(lesson.progressStatus || '');
                              setAssignment(lesson.assignment || '');
                            } else {
                              setSelectedClass(null);
                              setProgressPage('');
                              setProgressStatus('');
                              setAssignment('');
                            }
                          }}
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '9999px',
                            border: isSelected 
                              ? '1px solid var(--Color-fill-brand-color, #FF6F0F)' 
                              : hasLesson
                              ? '1px solid var(--Color-line-alternative, #E9E9E9)'
                              : '1px dashed var(--Color-line-alternative, #E9E9E9)',
                            background: isSelected
                              ? 'var(--Color-fill-brand-color, #FF6F0F)'
                              : hasLesson
                              ? 'var(--Color-background-normal, white)'
                              : 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}
                        >
                          {isSelected && lesson && (
                            <div style={{
                              color: 'white',
                              fontSize: '10px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '15px'
                            }}>
                              {lesson.grade}-{lesson.class}
                            </div>
                          )}
                          {hasLesson && !isSelected && lesson && (
                            <div style={{
                              color: 'var(--Color-text-normal, #161616)',
                              fontSize: '10px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '15px'
                            }}>
                              {lesson.grade}-{lesson.class}
                            </div>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* 선택된 교시 정보 */}
                {selectedPeriod && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <span style={{
                      color: '#FF3838',
                      fontSize: '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '24px'
                    }}>
                      {selectedClass ? `${selectedClass.grade}학년 ${selectedClass.class}반` : '학급을 선택하세요'} {selectedPeriod}교시
                    </span>
                    <span style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '24px'
                    }}>
                      수업 진도를 등록합니다.
                    </span>
                  </div>
                )}
              </div>

              {/* 진도 입력 폼 */}
              {selectedPeriod && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  {!selectedClass && (
                    <div style={{ position: 'relative' }} data-dropdown>
                      <button
                        onClick={() => setClassFormDropdownOpen(!classFormDropdownOpen)}
                        style={{
                          width: '200px',
                          height: '40px',
                          padding: '12px 16px',
                          border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px',
                          background: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: selectedClass ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)'
                        }}
                      >
                        <span>학급 선택</span>
                        <DownIcon width={24} height={24} />
                      </button>
                      {classFormDropdownOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '4px',
                          background: 'white',
                          border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 1000,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          width: '200px'
                        }}>
                          {subjectClasses.map((cls) => (
                            <button
                              key={`${cls.grade}-${cls.class}`}
                              onClick={() => {
                                setSelectedClass({ grade: cls.grade, class: cls.class });
                                setClassFormDropdownOpen(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: 'none',
                                background: 'transparent',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontFamily: 'Pretendard',
                                fontWeight: '400',
                                color: 'var(--Color-text-normal, #161616)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--Color-background-sub, #F8F8F8)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              {cls.grade}학년 {cls.class}반
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setProgressPageDropdownOpen(!progressPageDropdownOpen)}
                        style={{
                          width: '123px',
                          height: '40px',
                          padding: '12px 16px',
                          border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px',
                          background: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: progressPage ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                          textAlign: 'left'
                        }}
                      >
                        <span>{progressPage || '진도 페이지'}</span>
                        <DownIcon width={24} height={24} />
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="진도 현황 (예: 3단원 국어 기초)"
                      value={progressStatus}
                      onChange={(e) => setProgressStatus(e.target.value)}
                      style={{
                        flex: 1,
                        height: '40px',
                        padding: '12px 16px',
                        border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: '19.5px',
                        color: progressStatus ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)'
                      }}
                    />
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => setAssignmentDropdownOpen(!assignmentDropdownOpen)}
                        style={{
                          width: '238px',
                          height: '40px',
                          padding: '12px 16px',
                          border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px',
                          background: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: assignment ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                          textAlign: 'left'
                        }}
                      >
                        <span>{assignment || '과제'}</span>
                        <DownIcon width={24} height={24} />
                      </button>
                    </div>
                    <Button
                      variant="submit"
                      width="129px"
                      height="40px"
                      size="medium"
                      onClick={handleSubmit}
                    >
                      등록
                    </Button>
                  </div>
                </div>
              )}

              {/* 진도 기록 테이블 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto'
              }}>
                {/* 테이블 헤더 */}
                <div style={{
                  display: 'flex',
                  height: '32px',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  paddingBottom: '12px'
                }}>
                  <div style={{ width: '76px', paddingLeft: '16px', paddingRight: '16px' }}>
                    <span style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      학급
                    </span>
                  </div>
                  <div style={{ width: '105px', paddingLeft: '8px', paddingRight: '8px' }}>
                    <span style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      날짜
                    </span>
                  </div>
                  <div style={{ flex: 1, paddingLeft: '8px', paddingRight: '8px' }}>
                    <span style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      진도 현황
                    </span>
                  </div>
                  <div style={{ width: '93px', paddingLeft: '8px', paddingRight: '8px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}>
                        진행도
                      </span>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}>
                        <span style={{ fontSize: '8px', lineHeight: '1', color: 'var(--Color-text-assistive, #989898)' }}>↑</span>
                        <span style={{ fontSize: '8px', lineHeight: '1', color: 'var(--Color-text-assistive, #989898)' }}>↓</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ width: '196px', paddingLeft: '8px', paddingRight: '8px' }}>
                    <span style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      과제
                    </span>
                  </div>
                  <div style={{ width: '40px', paddingLeft: '8px', paddingRight: '8px' }}>
                    <span style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      관리
                    </span>
                  </div>
                </div>

                {/* 테이블 바디 */}
                {filteredLessons.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard'
                  }}>
                    {viewMode === 'today' ? '오늘 등록된 진도가 없습니다.' : '등록된 진도가 없습니다.'}
                  </div>
                ) : (
                  filteredLessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      style={{
                        display: 'flex',
                        height: '45px',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        paddingTop: '12px',
                        paddingBottom: '12px'
                      }}
                    >
                      <div style={{ width: '76px', paddingLeft: '18px', paddingRight: '18px' }}>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {lesson.grade}-{lesson.class}
                        </span>
                      </div>
                      <div style={{ width: '105px', paddingLeft: '8px', paddingRight: '8px' }}>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {formatDate(lesson.date)}
                        </span>
                      </div>
                      <div style={{ flex: 1, paddingLeft: '8px', paddingRight: '8px' }}>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {lesson.progressPage && `p${lesson.progressPage} `}
                          {lesson.progressStatus}
                        </span>
                      </div>
                      <div style={{ width: '93px', paddingLeft: '8px', paddingRight: '8px' }}>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {lesson.progressPercentage}%
                        </span>
                      </div>
                      <div style={{ width: '196px', paddingLeft: '8px', paddingRight: '8px' }}>
                        <span style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '21px'
                        }}>
                          {lesson.assignment}
                        </span>
                      </div>
                      <div style={{
                        width: '40px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <button
                          onClick={() => handleDelete(lesson.id)}
                          style={{
                            width: '20px',
                            height: '20px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <TrashIcon width={16} height={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>

        </div>
        ) : (
          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'stretch',
            width: '100%',
            minWidth: 0
          }}>
            {/* 왼쪽: 교과 시간표 */}
            <Card
              width="411px"
              padding="20px"
              gap="20px"
              style={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <h2 style={{
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    fontSize: '18px',
                    fontFamily: 'Pretendard',
                    fontWeight: '600',
                    lineHeight: '27px',
                    margin: 0
                  }}>
                    교과 시간표
                  </h2>
                  <div style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '19.5px'
                  }}>
                    2025 1학기
                  </div>
                </div>
                <Button
                  variant="line"
                  width="auto"
                  height="32px"
                  size="small"
                  leftIcon={<EditIcon width={14} height={14} />}
                  onClick={() => {}}
                >
                  시간표 추가하기
                </Button>
              </div>

              {/* 시간표 그리드 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {/* 헤더 */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '80px repeat(5, 1fr)',
                  gap: '8px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                }}>
                  <div></div>
                  {['월', '화', '수', '목', '금'].map(day => (
                    <div
                      key={day}
                      style={{
                        textAlign: 'center',
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '19.5px'
                      }}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* 교시별 행 */}
                {[
                  { period: 1, time: '09:00' },
                  { period: 2, time: '10:00' },
                  { period: 3, time: '11:00' },
                  { period: 4, time: '12:00' },
                  { period: null, isLunch: true },
                  { period: 5, time: '13:40' },
                  { period: 6, time: '14:40' },
                  { period: 7, time: '15:40' }
                ].map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px repeat(5, 1fr)',
                      gap: '8px',
                      padding: '8px 0'
                    }}
                  >
                    {item.isLunch ? (
                      <>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '19.5px'
                        }}>
                          점심시간
                        </div>
                        <div style={{
                          gridColumn: 'span 5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '19.5px'
                        }}>
                          <span>☀️</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '19.5px'
                        }}>
                          {item.period}교시 {item.time}
                        </div>
                        {['월', '화', '수', '목', '금'].map(day => {
                          // 실제 데이터는 나중에 연결
                          const cellData: string | null = null; // 예: timetableData?.[item.period]?.[day]
                          let cellColor = 'var(--Color-text-assistive, #989898)';
                          let cellBackground = 'transparent';
                          let cellText = '-';
                          
                          if (cellData !== null && cellData !== undefined) {
                            const dataStr: string = cellData;
                            cellText = dataStr;
                            cellBackground = 'var(--Color-background-sub, #F8F8F8)';
                            if (dataStr.includes('1-2')) {
                              cellColor = 'var(--Color-text-brand-color, #FF6F0F)';
                            } else {
                              cellColor = 'var(--Color-text-normal, #161616)';
                            }
                          }
                          
                          return (
                            <div
                              key={day}
                              style={{
                                textAlign: 'center',
                                color: cellColor,
                                fontSize: '13px',
                                fontFamily: 'Pretendard',
                                fontWeight: '400',
                                lineHeight: '19.5px',
                                padding: '4px',
                                borderRadius: '4px',
                                background: cellBackground
                              }}
                            >
                              {cellText}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* 오른쪽: 수업 계획 및 교과 과제 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              flex: 1,
              minWidth: 0
            }}>
              {/* 수업 계획 카드 */}
              <Card
                padding="20px"
                gap="20px"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    fontSize: '18px',
                    fontFamily: 'Pretendard',
                    fontWeight: '600',
                    lineHeight: '27px',
                    margin: 0
                  }}>
                    수업 계획
                  </h2>
                  <Button
                    variant="line"
                    width="auto"
                    height="32px"
                    size="small"
                    onClick={() => {}}
                  >
                    수업 계획 설정하기
                  </Button>
                </div>

                {/* 수업 계획 리스트 (수평 스크롤) */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  overflowX: 'auto',
                  paddingBottom: '8px',
                  minWidth: 0
                }}>
                  {/* 예시 데이터 - 실제로는 state나 props로 관리 */}
                  {[
                    {
                      grade: 1,
                      semester: 1,
                      textbook: '국어 1-1(비상)',
                      midterm: 'P56',
                      final: 'P126',
                      units: ['5단원 고전시가', '11단원 고전소설']
                    },
                    {
                      grade: 3,
                      semester: 1,
                      textbook: '국어 3-1(비상)',
                      midterm: 'P56',
                      final: 'P126',
                      units: ['5단원 고전시가', '11단원 고전소설']
                    }
                  ].map((plan, index) => (
                    <Card
                      key={index}
                      padding="16px"
                      gap="12px"
                      style={{
                        minWidth: '280px',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                      }}
                    >
                      <button
                        style={{
                          position: 'absolute',
                          top: '16px',
                          right: '16px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={() => {}}
                      >
                        <ThreeDotLengthIcon width={3} height={13} />
                      </button>
                      
                      {/* 이미지 영역 (교과서 이미지) */}
                      <div style={{
                        width: '100%',
                        height: '120px',
                        background: 'var(--Color-background-sub, #F8F8F8)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400'
                        }}>
                          교과서 이미지
                        </div>
                      </div>

                      {/* 제목 */}
                      <div style={{
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        {plan.grade}학년 {plan.semester}학기
                      </div>

                      {/* 교과서 정보 */}
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: '19.5px'
                      }}>
                        교과서: {plan.textbook}
                      </div>

                      {/* 상세 정보 */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px',
                        marginTop: '4px'
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <div style={{
                            color: 'var(--Color-text-normal, #161616)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '19.5px'
                          }}>
                            중간고사 {plan.midterm}
                          </div>
                          <div style={{
                            color: 'var(--Color-text-normal, #161616)',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '19.5px'
                          }}>
                            기말고사 {plan.final}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          {plan.units.map((unit, idx) => (
                            <div
                              key={idx}
                              style={{
                                color: 'var(--Color-text-normal, #161616)',
                                fontSize: '13px',
                                fontFamily: 'Pretendard',
                                fontWeight: '400',
                                lineHeight: '19.5px'
                              }}
                            >
                              {unit}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>

              {/* 교과 과제 카드 */}
              <Card
                padding="20px"
                gap="20px"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    color: 'var(--Color-text-strong, #0D0D0D)',
                    fontSize: '18px',
                    fontFamily: 'Pretendard',
                    fontWeight: '600',
                    lineHeight: '27px',
                    margin: 0
                  }}>
                    교과 과제
                  </h2>
                  <Button
                    variant="line"
                    width="auto"
                    height="32px"
                    size="small"
                    onClick={() => {}}
                  >
                    교과 과제 등록하기
                  </Button>
                </div>

                {/* 과제 리스트 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {[
                    {
                      id: 1,
                      description: '매일 영단어 10개 암기!',
                      type: '수행평가',
                      startDate: '2025.05.08',
                      endDate: '2025.08.15',
                      grade: 1
                    },
                    {
                      id: 2,
                      description: '매일 영단어 10개 암기!',
                      type: '수행평가',
                      startDate: '2025.05.08',
                      endDate: '2025.08.15',
                      grade: 1
                    },
                    {
                      id: 3,
                      description: '매일 영단어 10개 암기!',
                      type: '수행평가',
                      startDate: '2025.05.08',
                      endDate: '2025.08.15',
                      grade: 1
                    }
                  ].map((assignment) => (
                    <div
                      key={assignment.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '16px',
                        background: 'var(--Color-background-sub, #F8F8F8)',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '9999px',
                        background: 'var(--Color-fill-brand-color, #FF6F0F)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '21px',
                        flexShrink: 0
                      }}>
                        {assignment.id}
                      </div>
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-strong, #0D0D0D)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '21px'
                        }}>
                          {assignment.description}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '18px'
                          }}>
                            {assignment.type}
                          </span>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '18px'
                          }}>
                            {assignment.startDate} ~ {assignment.endDate}
                          </span>
                          <span style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '18px'
                          }}>
                            {assignment.grade}학년
                          </span>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexShrink: 0
                      }}>
                        <button
                          onClick={() => {}}
                          style={{
                            width: '32px',
                            height: '32px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--Color-background-sub, #F8F8F8)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <EditIcon width={16} height={16} />
                        </button>
                        <button
                          onClick={() => {}}
                          style={{
                            width: '32px',
                            height: '32px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--Color-background-sub, #F8F8F8)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <TrashIcon width={16} height={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* 진도 추가 모달 */}
      {showAddProgressModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}
        onClick={() => setShowAddProgressModal(false)}
        >
          <div
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            style={{
              width: '600px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
          <Card
            padding="32px"
            gap="24px"
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              {/* 모달 헤더 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '20px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: '30px',
                  margin: 0
                }}>
                  진도 추가
                </h2>
                <button
                  onClick={() => setShowAddProgressModal(false)}
                  style={{
                    width: '24px',
                    height: '24px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span style={{ fontSize: '24px', color: 'var(--Color-text-assistive, #989898)' }}>×</span>
                </button>
              </div>

              {/* 진도 입력 폼 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {/* 학급 정보 */}
                {selectedClass && (
                  <div style={{
                    padding: '12px 16px',
                    background: 'var(--Color-background-sub, #F8F8F8)',
                    borderRadius: '8px',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px'
                  }}>
                    {selectedClass.grade}학년 {selectedClass.class}반
                  </div>
                )}

                {/* 날짜 선택 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px'
                  }}>
                    날짜
                  </label>
                  <input
                    type="date"
                    value={selectedDate.toISOString().split('T')[0]}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      setSelectedDate(newDate);
                      setSelectedWeek(getWeekNumber(newDate));
                    }}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '12px 16px',
                      border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '19.5px',
                      color: 'var(--Color-text-normal, #161616)'
                    }}
                  />
                </div>

                {/* 교시 선택 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px'
                  }}>
                    교시
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {[1, 2, 3, 4, 5, 6, 7].map(period => (
                      <button
                        key={period}
                        onClick={() => setSelectedPeriod(period)}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: selectedPeriod === period
                            ? '1px solid var(--Color-fill-brand-color, #FF6F0F)'
                            : '1px solid var(--Color-line-alternative, #E9E9E9)',
                          borderRadius: '8px',
                          background: selectedPeriod === period
                            ? 'var(--Color-fill-brand-color, #FF6F0F)'
                            : 'white',
                          color: selectedPeriod === period
                            ? 'white'
                            : 'var(--Color-text-normal, #161616)',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '21px',
                          cursor: 'pointer'
                        }}
                      >
                        {period}교시
                      </button>
                    ))}
                  </div>
                </div>

                {/* 진도 페이지 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px'
                  }}>
                    진도 페이지
                  </label>
                  <input
                    type="text"
                    placeholder="진도 페이지"
                    value={progressPage}
                    onChange={(e) => setProgressPage(e.target.value)}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '12px 16px',
                      border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px',
                      color: progressPage ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)'
                    }}
                  />
                </div>

                {/* 진도 현황 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px'
                  }}>
                    진도 현황
                  </label>
                  <input
                    type="text"
                    placeholder="진도 현황 (예: 3단원 국어 기초)"
                    value={progressStatus}
                    onChange={(e) => setProgressStatus(e.target.value)}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '12px 16px',
                      border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '19.5px',
                      color: progressStatus ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)'
                    }}
                  />
                </div>

                {/* 과제 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px'
                  }}>
                    과제
                  </label>
                  <input
                    type="text"
                    placeholder="과제"
                    value={assignment}
                    onChange={(e) => setAssignment(e.target.value)}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '12px 16px',
                      border: '1px solid var(--Color-line-alternative, #E9E9E9)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px',
                      color: assignment ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)'
                    }}
                  />
                </div>
              </div>

              {/* 버튼 영역 */}
              <div style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end'
              }}>
                <Button
                  variant="cancel"
                  width="auto"
                  height="40px"
                  size="medium"
                  onClick={() => {
                    setShowAddProgressModal(false);
                    setSelectedClass(null);
                    setSelectedPeriod(null);
                    setProgressPage('');
                    setProgressStatus('');
                    setAssignment('');
                  }}
                >
                  취소
                </Button>
                <Button
                  variant="submit"
                  width="auto"
                  height="40px"
                  size="medium"
                  onClick={async () => {
                    if (!selectedClass || !selectedPeriod) {
                      setError('학급과 교시를 선택해주세요.');
                      setTimeout(() => setError(null), 3000);
                      return;
                    }
                    try {
                      await handleSubmit();
                      setShowAddProgressModal(false);
                    } catch (err) {
                      // 에러는 handleSubmit에서 처리됨
                    }
                  }}
                >
                  등록
                </Button>
              </div>
            </div>
          </Card>
          </div>
        </div>
      )}

      {/* 에러/성공 메시지 */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          padding: '12px 16px',
          background: '#FF3838',
          color: 'white',
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: 'Pretendard',
          zIndex: 1000
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          padding: '12px 16px',
          background: '#4CAF50',
          color: 'white',
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily: 'Pretendard',
          zIndex: 1000
        }}>
          {success}
        </div>
      )}
    </div>
  );
};

export default SubjectLessonManagement;

